import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CloseIcon from '@mui/icons-material/Close';
import {
  downloadBlob,
  generateLabelRaw,
  getAllCategories,
  getAllPdvs,
  getAllProducts,
  getLabelModels,
  previewLabels,
  solicitarImpressaoEtiquetasPdv,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  normalizeSearchTerm,
  PRODUCT_SEARCH_HINT,
  resolverProdutosPorCodigo,
  termoEhBuscaParcial,
} from '../../utils/productSearch';
import PageHeader from '../common/PageHeader';
import ResponsiveTable from '../common/ResponsiveTable';

const currency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const QTDE_BUSCA = 20;

export default function LabelsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const seedAplicado = useRef(false);
  const [fila, setFila] = useState([]);
  const [models, setModels] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [modeloId, setModeloId] = useState('');
  const [colunas, setColunas] = useState(2);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [pdvId, setPdvId] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const [buscaText, setBuscaText] = useState('');
  const [grupoId, setGrupoId] = useState('');
  const [consulta, setConsulta] = useState({ termo: '', grupoId: '' });
  const [resultados, setResultados] = useState([]);
  const [buscou, setBuscou] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [paginaBusca, setPaginaBusca] = useState(1);
  const [totalPaginasBusca, setTotalPaginasBusca] = useState(1);
  const [totalBusca, setTotalBusca] = useState(0);
  const [marcados, setMarcados] = useState(() => new Set());
  const [avisoBusca, setAvisoBusca] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [modelos, pdvRes, categorias] = await Promise.all([
          getLabelModels(),
          getAllPdvs(),
          getAllCategories({ indice: 1, qtde: 200, ordenar: 'descricao', direcao: 'asc' }),
        ]);
        setModels(modelos.data || []);
        if (modelos.data?.[0]) setModeloId(String(modelos.data[0].id));
        const listaPdvs = Array.isArray(pdvRes.data) ? pdvRes.data : [];
        setPdvs(
          listaPdvs.filter(
            (p) => p.geraEtiquetas && (p.impressoraEtiquetas || '').trim(),
          ),
        );
        const listaGrupos = categorias.data?.items || categorias.data || [];
        setGrupos(Array.isArray(listaGrupos) ? listaGrupos : []);
      } catch (e) {
        setError(getApiErrorMessage(e, 'Não foi possível carregar modelos e grupos.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (seedAplicado.current) return;
    const seed = location.state?.produtos;
    if (!Array.isArray(seed) || seed.length === 0) return;
    seedAplicado.current = true;
    const vistos = new Set();
    const itens = [];
    seed.forEach((p) => {
      if (!p?.id || vistos.has(p.id)) return;
      vistos.add(p.id);
      itens.push({
        id: p.id,
        codigo: p.codigo || '',
        descricao: p.descricao || '',
        ean: p.ean || '',
        quantidade: Math.max(1, Number(p.quantidade) || 1),
      });
    });
    if (itens.length) {
      setFila(itens);
      setOkMsg(`${itens.length} produto(s) vindos da importação da NF-e.`);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  const idsNaFila = useMemo(() => new Set(fila.map((p) => p.id)), [fila]);

  const elegiveis = useMemo(
    () => resultados.filter((p) => !idsNaFila.has(p.id)),
    [resultados, idsNaFila],
  );

  const todosMarcados = elegiveis.length > 0 && elegiveis.every((p) => marcados.has(p.id));
  const algumMarcado = elegiveis.some((p) => marcados.has(p.id));

  const adicionar = (produtos) => {
    const lista = (Array.isArray(produtos) ? produtos : [produtos]).filter((p) => p?.id);
    const vistos = new Set();
    const novos = [];
    let repetidos = 0;
    lista.forEach((p) => {
      if (idsNaFila.has(p.id) || vistos.has(p.id)) {
        repetidos += 1;
        return;
      }
      vistos.add(p.id);
      novos.push({
        id: p.id,
        codigo: p.codigo,
        descricao: p.descricao,
        ean: p.ean,
        quantidade: 1,
      });
    });
    if (novos.length) {
      setFila((prev) => {
        const ids = new Set(prev.map((p) => p.id));
        const incluir = novos.filter((p) => !ids.has(p.id));
        return incluir.length ? [...prev, ...incluir] : prev;
      });
    }
    if (novos.length && repetidos) {
      setAvisoBusca(`${novos.length} adicionado(s). ${repetidos} já estavam na impressão.`);
    } else if (novos.length) {
      setAvisoBusca(novos.length === 1 ? 'Produto adicionado à impressão.' : `${novos.length} produtos adicionados à impressão.`);
    } else if (repetidos) {
      setAvisoBusca('Esse produto já está na impressão.');
    }
    setMarcados(new Set());
  };

  const executarBusca = async (pagina, termoBruto, { adicionarSeUnico = false, grupo = grupoId } = {}) => {
    const termo = normalizeSearchTerm(termoBruto);
    if (!termo && !grupo) {
      setAvisoBusca('Informe um termo ou escolha um grupo.');
      return;
    }
    setBuscando(true);
    setError('');
    setAvisoBusca('');
    try {
      const { data } = await getAllProducts({
        indice: pagina,
        qtde: QTDE_BUSCA,
        termo,
        categoriaId: grupo,
        ativo: 'true',
        ordenar: 'descricao',
        direcao: 'asc',
      });
      const items = data?.items || [];
      setResultados(items);
      setPaginaBusca(data?.page || pagina);
      setTotalPaginasBusca(data?.totalPages || 1);
      setTotalBusca(data?.totalItems || 0);
      setBuscou(true);
      setConsulta({ termo, grupoId: grupo || '' });
      setMarcados(new Set());

      if (adicionarSeUnico && termo && !termoEhBuscaParcial(termoBruto)) {
        const resolvido = resolverProdutosPorCodigo(items, termo);
        if (resolvido.modo === 'unico') {
          adicionar(resolvido.produto);
          setBuscaText('');
        }
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao buscar produtos.'));
    } finally {
      setBuscando(false);
    }
  };

  const acionarBusca = () => {
    executarBusca(1, buscaText);
  };

  const onKeyDownBusca = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    executarBusca(1, buscaText, { adicionarSeUnico: true });
  };

  const mudarPaginaBusca = (_e, nova) => {
    executarBusca(nova, consulta.termo, { grupo: consulta.grupoId });
  };

  const alternarMarcado = (id) => {
    setMarcados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const alternarTodos = () => {
    setMarcados(() => {
      if (todosMarcados) return new Set();
      return new Set(elegiveis.map((p) => p.id));
    });
  };

  const adicionarMarcados = () => {
    const escolhidos = resultados.filter((p) => marcados.has(p.id) && !idsNaFila.has(p.id));
    if (!escolhidos.length) return;
    adicionar(escolhidos);
  };

  const atualizarQuantidade = (id, valor) => {
    setFila((prev) => prev.map((p) => (p.id === id ? { ...p, quantidade: valor } : p)));
  };

  const removerDaFila = (id) => {
    setFila((prev) => prev.filter((p) => p.id !== id));
  };

  const itens = fila.map((p) => ({
    produtoId: p.id,
    quantidade: Math.max(1, Number(p.quantidade || 1)),
  }));

  const buildPayload = () => ({
    itens,
    modeloId: modeloId ? Number(modeloId) : null,
    colunas: Number(colunas) || 2,
  });

  const openPreview = async () => {
    if (!itens.length) {
      setError('Adicione ao menos um produto para imprimir.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await previewLabels(buildPayload());
      setPreview(response.data || []);
      setPreviewOpen(true);
    } catch (e) {
      setError(e?.response?.data?.error || 'Falha ao montar as etiquetas.');
    } finally {
      setLoading(false);
    }
  };

  const printSheet = () => {
    const popup = window.open('', '_blank', 'width=900,height=700');
    if (!popup) {
      setError('Libere pop-ups para imprimir.');
      return;
    }
    const cards = preview
      .map(
        (p) => `<div class="label">
          <div class="desc">${escapeHtml(p.descricao)}</div>
          <div class="price">${currency(p.precoPromocao > 0 ? p.precoPromocao : p.precoVarejo)}</div>
          <svg class="barcode" jsbarcode-format="CODE128" jsbarcode-value="${escapeHtml(
            p.ean || p.codigo
          )}" jsbarcode-displayvalue="true" jsbarcode-height="36" jsbarcode-margin="0"></svg>
          <div class="code">${escapeHtml(p.codigo)} · ${escapeHtml(p.unidade || '')}</div>
        </div>`
      )
      .join('');
    popup.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      <style>
        @page { margin: 8mm; }
        body { font: 11px Arial; color:#111; }
        .grid { display:grid; grid-template-columns: repeat(${Number(colunas) || 2}, 1fr); gap: 8px; }
        .label { border: 1px dashed #999; border-radius: 4px; padding: 8px; break-inside: avoid; text-align:center; }
        .desc { font-weight:700; min-height: 28px; }
        .price { font-size: 18px; font-weight: 900; margin: 4px 0; }
        .code { font-size: 10px; color:#444; }
        svg { max-width: 100%; }
      </style></head><body>
      <div class="grid">${cards}</div>
      <script>JsBarcode('.barcode').init(); setTimeout(() => window.print(), 400);<\/script>
      </body></html>`);
    popup.document.close();
  };

  const downloadRaw = async () => {
    if (!itens.length) {
      setError('Adicione ao menos um produto para imprimir.');
      return;
    }
    if (!modeloId) {
      setError('Selecione um modelo de etiqueta para gerar o arquivo RAW.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await generateLabelRaw(buildPayload());
      const blob = new Blob([response.data.conteudo || ''], {
        type: 'text/plain;charset=utf-8',
      });
      downloadBlob(blob, response.data.nomeArquivo || 'etiquetas.txt');
    } catch (e) {
      setError(e?.response?.data?.error || 'Falha ao gerar o arquivo RAW.');
    } finally {
      setLoading(false);
    }
  };

  const enviarPdv = async () => {
    setError('');
    setOkMsg('');
    if (!itens.length) {
      setError('Adicione ao menos um produto para imprimir.');
      return;
    }
    if (!modeloId) {
      setError('Selecione um modelo de etiqueta.');
      return;
    }
    if (!pdvId) {
      setError('Selecione um PDV com impressora de etiquetas configurada.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await solicitarImpressaoEtiquetasPdv(
        Number(pdvId),
        buildPayload(),
      );
      setOkMsg(
        `Etiquetas enviadas ao PDV. Impressora: ${data?.impressora || '—'}. ` +
          `${data?.quantidadeEtiquetas ?? '?'} etiqueta(s) — na próxima sincronização o terminal imprime o RAW.`,
      );
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha ao enviar etiquetas ao PDV.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Gerar etiquetas"
        subtitle="Busque os produtos, lance na lista e imprima em folha, baixe o RAW ou envie ao PDV."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Buscar produtos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Mesma busca da listagem de produtos: sem espaço no início a correspondência é exata;
          com espaço no início, contém. Enter ou o leitor inclui o produto quando houver um único código.
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="filtro-grupo-etiqueta">Grupo</InputLabel>
            <Select
              labelId="filtro-grupo-etiqueta"
              label="Grupo"
              value={grupoId}
              onChange={(e) => setGrupoId(e.target.value)}
            >
              <MenuItem value=""><em>Todos</em></MenuItem>
              {grupos.map((g) => (
                <MenuItem key={g.id} value={String(g.id)}>{g.descricao}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder={PRODUCT_SEARCH_HINT}
            value={buscaText}
            onChange={(e) => setBuscaText(e.target.value)}
            onKeyDown={onKeyDownBusca}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: buscaText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setBuscaText('')} aria-label="Limpar busca">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flex: 1, minWidth: 260 }}
          />
          <Button variant="outlined" onClick={acionarBusca} disabled={buscando}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </Button>
        </Stack>

        {avisoBusca && (
          <Alert severity="info" sx={{ mt: 1.5 }} onClose={() => setAvisoBusca('')}>
            {avisoBusca}
          </Alert>
        )}

        {buscou && (
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, mb: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="contained"
              startIcon={<PlaylistAddIcon />}
              disabled={!algumMarcado}
              onClick={adicionarMarcados}
            >
              Adicionar selecionados ({[...marcados].filter((id) => !idsNaFila.has(id)).length})
            </Button>
            <Typography variant="body2" color="text.secondary">
              {totalBusca} produto(s) encontrado(s)
            </Typography>
          </Stack>
        )}

        {buscou && (
          <ResponsiveTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={todosMarcados}
                      indeterminate={algumMarcado && !todosMarcados}
                      disabled={!elegiveis.length}
                      onChange={alternarTodos}
                    />
                  </TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>EAN</TableCell>
                  <TableCell>Grupo</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {resultados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      Nenhum produto encontrado
                    </TableCell>
                  </TableRow>
                ) : resultados.map((p) => {
                  const jaNaFila = idsNaFila.has(p.id);
                  return (
                    <TableRow key={p.id} hover selected={marcados.has(p.id)}>
                      <TableCell padding="checkbox" data-label="Selecionar">
                        <Checkbox
                          checked={jaNaFila || marcados.has(p.id)}
                          disabled={jaNaFila}
                          onChange={() => alternarMarcado(p.id)}
                        />
                      </TableCell>
                      <TableCell data-label="Código">{p.codigo || '-'}</TableCell>
                      <TableCell data-label="Descrição">{p.descricao}</TableCell>
                      <TableCell data-label="EAN">{p.ean || '-'}</TableCell>
                      <TableCell data-label="Grupo">{p.categoriaNome || '-'}</TableCell>
                      <TableCell align="right" data-actions="">
                        {jaNaFila ? (
                          <Chip size="small" label="Na impressão" variant="outlined" />
                        ) : (
                          <Tooltip title="Adicionar à impressão">
                            <IconButton color="primary" onClick={() => adicionar(p)} aria-label="Adicionar">
                              <AddIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ResponsiveTable>
        )}

        {buscou && totalPaginasBusca > 1 && (
          <Stack spacing={0.5} sx={{ mt: 1.5, alignItems: 'center' }}>
            <Pagination
              count={totalPaginasBusca}
              page={paginaBusca}
              onChange={mudarPaginaBusca}
              color="primary"
              size="small"
            />
          </Stack>
        )}

        {!buscou && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            A lista de impressão começa vazia. Busque e use + ou a seleção para lançar os itens.
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Modelo RAW"
            value={modeloId}
            onChange={(e) => setModeloId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Sem modelo</MenuItem>
            {models.map((m) => (
              <MenuItem key={m.id} value={String(m.id)}>{m.descricao}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Colunas na folha"
            value={colunas}
            onChange={(e) => setColunas(e.target.value)}
            sx={{ minWidth: 150 }}
          >
            {[1, 2, 3, 4].map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={openPreview} disabled={loading || !fila.length}>
            Preparar impressão
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadRaw} disabled={loading || !fila.length}>
            Baixar RAW
          </Button>
          <TextField
            select
            size="small"
            label="PDV (etiquetas)"
            value={pdvId}
            onChange={(e) => setPdvId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">Selecione</MenuItem>
            {pdvs.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.descricao} — {p.impressoraEtiquetas}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SendIcon />}
            onClick={enviarPdv}
            disabled={loading || !pdvs.length || !fila.length}
          >
            Enviar ao PDV
          </Button>
        </Stack>
      </Paper>

      {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {(loading || buscando) && <LinearProgress sx={{ mb: 2 }} />}

      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Itens para impressão ({fila.length})
      </Typography>
      <ResponsiveTable>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>EAN</TableCell>
              <TableCell align="right">Qtd. etiquetas</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {fila.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum item lançado
                </TableCell>
              </TableRow>
            ) : fila.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell data-label="Código">{p.codigo || '-'}</TableCell>
                <TableCell data-label="Descrição">{p.descricao}</TableCell>
                <TableCell data-label="EAN">{p.ean || '-'}</TableCell>
                <TableCell align="right" data-label="Qtd. etiquetas">
                  <TextField
                    size="small"
                    type="number"
                    value={p.quantidade}
                    onChange={(e) => atualizarQuantidade(p.id, e.target.value)}
                    inputProps={{ min: 1, max: 200 }}
                    sx={{ width: 90 }}
                  />
                </TableCell>
                <TableCell align="right" data-actions="">
                  <Tooltip title="Remover">
                    <IconButton color="error" onClick={() => removerDaFila(p.id)} aria-label="Remover">
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{preview.length} etiqueta(s) pronta(s)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confira e imprima. No diálogo do navegador, use “Salvar como PDF” se quiser arquivo.
          </Typography>
          {preview.slice(0, 12).map((p, index) => (
            <Paper key={`${p.codigo}-${index}`} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              <Typography fontWeight={700}>{p.descricao}</Typography>
              <Typography>
                {currency(p.precoPromocao > 0 ? p.precoPromocao : p.precoVarejo)} · {p.codigo}
              </Typography>
            </Paper>
          ))}
          {preview.length > 12 && (
            <Typography variant="caption">… e mais {preview.length - 12}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={printSheet}>
            Imprimir / PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
